/**
 * Admin Developer Service
 * 
 * Provides fallback developer data for admin users when A2A is unavailable
 * Admin users have full access via RLS policies, so we can query Supabase directly
 */

import { supabase } from '../lib/supabase';

/**
 * Get admin developer profile from Supabase
 * This is a fallback when A2A authentication fails
 * 
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Developer profile data
 */
export async function getAdminDeveloperProfile(userId) {
  try {
    // For admin users, we can query all developer data from Supabase
    // Admin RLS policies allow full access
    
    // Get developer data from app_developers table (if exists)
    let developer = null;
    let apps = [];
    
    try {
      const { data: devData, error: devError } = await supabase
        .from('app_developers')
        .select('*, apps(*)')
        .eq('user_id', userId)
        .single();

      if (!devError) {
        developer = devData;
        apps = devData?.apps || [];
      } else if (devError.code !== 'PGRST116') {
        // PGRST116 = no rows returned, which is OK for new admins
        console.warn('[Admin Developer Service] Error fetching developer:', devError);
      }
    } catch (err) {
      // Table might not exist, that's OK
      console.warn('[Admin Developer Service] app_developers table not accessible:', err.message);
    }

    // Get apps count
    const publishedApps = apps.filter(app => 
      app.status === 'published' || 
      app.status === 'active' || 
      app.published_at !== null
    );

    // Calculate totals from apps
    const totalDownloads = apps.reduce((sum, app) => sum + (app.download_count || app.downloads || 0), 0);
    const totalRevenue = apps.reduce((sum, app) => sum + (app.revenue || app.total_revenue || 0), 0);

    // Get API keys from developer_api_keys table (admin can see all)
    let apiKeys = [];
    try {
      const { data: keysData } = await supabase
        .from('developer_api_keys')
        .select('*')
        .eq('user_id', userId);
      
      apiKeys = keysData || [];
    } catch (err) {
      // Table might not exist, that's OK
      console.warn('[Admin Developer Service] developer_api_keys table not accessible:', err.message);
    }

    return {
      developer_id: developer?.id || userId,
      id: developer?.id || userId,
      total_apps: apps.length,
      published_apps: publishedApps.length,
      total_downloads: totalDownloads,
      total_revenue: totalRevenue,
      apps: apps,
      api_keys: apiKeys || [],
      is_admin: true,
      has_full_access: true,
      verified: developer?.verified || true, // Admin is always verified
      company_name: developer?.company_name || 'System Administrator',
    };
  } catch (error) {
    console.error('[Admin Developer Service] Error:', error);
    // Return default admin profile
    return {
      developer_id: userId,
      id: userId,
      total_apps: 0,
      published_apps: 0,
      total_downloads: 0,
      total_revenue: 0,
      apps: [],
      api_keys: [],
      is_admin: true,
      has_full_access: true,
      verified: true,
      company_name: 'System Administrator',
    };
  }
}

/**
 * Get all developer projects for admin (admin can see all projects)
 * 
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of projects
 */
export async function getAdminProjects(userId) {
  try {
    const { data: projects, error } = await supabase
      .from('developer_projects')
      .select('*')
      .order('updated_at', { ascending: false });

    if (error) {
      console.error('[Admin Developer Service] Error fetching projects:', error);
      return [];
    }

    return projects || [];
  } catch (error) {
    console.error('[Admin Developer Service] Error:', error);
    return [];
  }
}

/**
 * Get all workflows for admin (admin can see all workflows)
 * 
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of workflows
 */
export async function getAdminWorkflows(userId) {
  try {
    const { data: workflows, error } = await supabase
      .from('developer_workflows')
      .select('*, developer_projects(*)')
      .order('updated_at', { ascending: false });

    if (error) {
      console.error('[Admin Developer Service] Error fetching workflows:', error);
      return [];
    }

    return workflows || [];
  } catch (error) {
    console.error('[Admin Developer Service] Error:', error);
    return [];
  }
}

